import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  CreateSessionPlanResponse,
  SessionPlan,
  SessionPlanFeedback,
  SessionPlanListItem,
  SessionPlanRunStatus,
  SubmitSessionPlanFeedbackInput,
} from 'types/SessionPlan';
import { axios } from 'utils/axios';

export const sessionPlanKeys = {
  all: ['session-plans'] as const,
  list: () => [...sessionPlanKeys.all, 'list'] as const,
  detail: (planId: string) => [...sessionPlanKeys.all, 'detail', planId] as const,
  feedback: (planId: string) => [...sessionPlanKeys.all, 'feedback', planId] as const,
};

const createSessionPlan = async (userInput: string): Promise<CreateSessionPlanResponse> => {
  const { data } = await axios.post<CreateSessionPlanResponse>('/session-plan', { userInput });
  return data;
};

export const useCreateSessionPlan = (): UseMutationResult<
  CreateSessionPlanResponse,
  unknown,
  string,
  unknown
> => {
  return useMutation({
    mutationKey: ['create-session-plan'],
    mutationFn: createSessionPlan,
  });
};

const getSessionPlanStatus = async (runId: string): Promise<SessionPlanRunStatus> => {
  const { data } = await axios.get<SessionPlanRunStatus>(`/session-plan/${runId}`);
  return data;
};

export const useGetSessionPlanStatus = (): UseMutationResult<
  SessionPlanRunStatus,
  unknown,
  string,
  unknown
> => {
  return useMutation({
    mutationKey: ['get-session-plan-status'],
    mutationFn: getSessionPlanStatus,
  });
};

const getSessionPlan = async (planId: string): Promise<SessionPlan> => {
  const { data } = await axios.get<SessionPlan>(`/session-plan/plans/${planId}`);
  return data;
};

export const useSessionPlan = (
  planId: string | null | undefined
): UseQueryResult<SessionPlan, unknown> => {
  return useQuery({
    queryKey: sessionPlanKeys.detail(planId ?? ''),
    queryFn: () => getSessionPlan(planId as string),
    enabled: !!planId,
    retry: 1,
  });
};

const listSessionPlans = async (): Promise<SessionPlanListItem[]> => {
  const { data } = await axios.get<SessionPlanListItem[]>('/session-plan/plans');
  return data;
};

export const useSessionPlans = (): UseQueryResult<SessionPlanListItem[], unknown> => {
  return useQuery({
    queryKey: sessionPlanKeys.list(),
    queryFn: listSessionPlans,
    retry: 1,
  });
};

const getSessionPlanFeedback = async (planId: string): Promise<SessionPlanFeedback | null> => {
  const { data } = await axios.get<SessionPlanFeedback | ''>(
    `/session-plan/plans/${planId}/feedback`
  );
  // Nest serializes a `null` response body as an empty string.
  return data || null;
};

export const useSessionPlanFeedback = (
  planId: string | null | undefined
): UseQueryResult<SessionPlanFeedback | null, unknown> => {
  return useQuery({
    queryKey: sessionPlanKeys.feedback(planId ?? ''),
    queryFn: () => getSessionPlanFeedback(planId as string),
    enabled: !!planId,
    retry: 1,
  });
};

export const useSubmitSessionPlanFeedback = (): UseMutationResult<
  SessionPlanFeedback,
  unknown,
  SubmitSessionPlanFeedbackInput,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['submit-session-plan-feedback'],
    mutationFn: async ({ planId, rating, comment }) => {
      const { data } = await axios.put<SessionPlanFeedback>(
        `/session-plan/plans/${planId}/feedback`,
        { rating, comment }
      );
      return data;
    },
    onSuccess: (data, { planId }) => {
      queryClient.setQueryData(sessionPlanKeys.feedback(planId), data);
    },
  });
};

export { getSessionPlan };
